import { db, Record } from './audioUploadDB';

// ── 백업 보관 정책 (PORTAL-12583) ──────────────────────────────────
// 업로드 실패로 백업된 녹음은 사용자가 복구하기 전까지 유일한 원본이다.
// 1개월 만에 예고 없이 지우면 사용자가 모른 채 녹음을 잃으므로 보관 기간을 늘렸다.
// 앱 진입 때 띄우던 삭제 예고(내려받기) 안내는 기획 요청으로 뺐다 — 대신 목록의
// 「업로드 실패」 라벨과 문서 화면의 실패 안내로 미복구 녹음을 드러낸다.
const RETENTION_DAYS = 90;

const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

// ── 문서당 복수 보관 (PORTAL-12582) ────────────────────────────────
// 예전엔 문서당 1건만 두고 덮어써서, 같은 문서에서 두 번 실패하면 먼저 백업된 녹음이 사라졌다.
// 실패할 때마다 새 레코드로 쌓고, 지울 때는 레코드 id 로만 지운다(문서 단위로 지우지 않는다).

/** 백업을 새 레코드로 쌓고 id 를 돌려준다 — 재시도 성공 시 이 id 만 지운다 */
export const saveToQueue = async (
  docId: string,
  title: string,
  recordFile: File,
  duration: string,
): Promise<number> => {
  const id = await db.records.add({
    docId,
    title,
    recordFile,
    duration,
    createdAt: new Date(),
  });
  notifyAudioQueueChanged();
  return id;
};

/**
 * 백업 큐가 바뀌었음을 같은 탭에 알린다 — 문서 화면의 실패 안내 · 목록 라벨이 다시 읽는다.
 * 쌓기 · 지우기 · 비정상 종료 녹음 되살리기(PORTAL-12587) 때 보낸다.
 */
export const AUDIO_QUEUE_CHANGED_EVENT = 'talkflow-audio-queue-changed';
export const notifyAudioQueueChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUDIO_QUEUE_CHANGED_EVENT));
  }
};

const UPLOAD_FAILED_DIALOG_PREFIX = 'record-upload-failed-';

/** 업로드 실패 안내창 id — 다른 경로가 그 백업을 복구하면 이 id 로 안내를 닫는다 (PORTAL-12584) */
export const uploadFailedDialogId = (id: number) =>
  `${UPLOAD_FAILED_DIALOG_PREFIX}${id}`;

/** 업로드 실패 안내창인지 — 어떤 백업의 안내든 떠 있는지 확인할 때 쓴다 */
export const isUploadFailedDialogId = (id?: string) =>
  !!id?.startsWith(UPLOAD_FAILED_DIALOG_PREFIX);

/** 백업 1건 — 다른 탭이 이미 복구해 지웠으면 undefined (PORTAL-12586) */
export const getQueuedRecordById = async (
  id: number,
): Promise<Record | undefined> => {
  return await db.records.get(id);
};

/** 문서의 미복구 백업 — 오래된 것부터 */
export const getQueuedRecords = async (docId: string): Promise<Record[]> => {
  return await db.records.where('docId').equals(docId).sortBy('createdAt');
};

export const deleteFromQueue = async (id: number): Promise<void> => {
  await db.records.delete(id);
  notifyAudioQueueChanged();
};

/** 미복구 백업이 있는 문서 id — 목록의 「업로드 실패」 라벨 (PORTAL-12583) */
export const getQueuedDocIds = async (): Promise<Set<string>> => {
  const keys = await db.records.orderBy('docId').uniqueKeys();
  return new Set(keys.map(String));
};

export const cleanupOldRecords = async (): Promise<void> => {
  await db.records.where('createdAt').below(daysAgo(RETENTION_DAYS)).delete();
};

// 삭제까지 남은 일수 — 0 이면 다음 정리 때 삭제된다.
export const getDaysUntilDeletion = (record: Record): number => {
  const deleteAt = new Date(record.createdAt);
  deleteAt.setDate(deleteAt.getDate() + RETENTION_DAYS);
  const remainMs = deleteAt.getTime() - Date.now();
  return Math.max(0, Math.ceil(remainMs / (24 * 60 * 60 * 1000)));
};
